/**
 * <EmployeesResetPasswordDialog> — admin/РО issues a 1h reset link.
 *
 * Phase-2 Wave-4 W4.T5. Two-step Dialog (research-A §2.5):
 *
 *   Step 1: confirm — «Создать одноразовую ссылку для сброса пароля?»
 *           with body explaining the 1h TTL.
 *   Step 2: reveal  — TokenLinkDisplay with copy button + countdown +
 *           secondary «Отправить по email» button (Q64).
 *
 * RBAC matrix lives outside this component (the calling list / detail
 * modal hides the «Сбросить пароль» action when the actor is not
 * entitled). The BE is the firm guard.
 *
 * The send-email button posts to `/auth/password-reset/send-email` with
 * the raw token returned by `/issue`. The endpoint returns
 * `{ ok: boolean, error?: string }` per W1.T9 (research-B §F-03):
 *   - `{ ok: true }`                       → success toast.
 *   - `{ ok: false, error: 'smtp.unconfigured' }` → warning toast with a
 *                                            hint for the supervisor.
 *   - `{ ok: false, error: <other> }`      → error toast + inline alert.
 */
import * as React from 'react';
import { Mail } from 'lucide-react';

import type { UserDetail } from '@aris/shared';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { ApiError } from '@/lib/fetcher';

import { useIssueResetMutation, useSendResetEmailMutation } from '@/api/password-reset';

import { TokenLinkDisplay } from './token-link-display';

export interface EmployeesResetPasswordDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  user: UserDetail | null;
}

interface RevealState {
  resetUrl: string;
  expiresAt: string;
  /** Raw token — needed by the «Отправить по email» button. The BE only
   *  hashes the token at storage time, so the FE must keep the raw value
   *  in component state until it dispatches the send-email request. */
  rawToken: string;
}

/** Extract the raw token from a reset URL (the trailing path segment). */
export function extractRawToken(resetUrl: string): string {
  const trimmed = resetUrl.replace(/[?#].*$/, '');
  const parts = trimmed.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? '';
}

export function EmployeesResetPasswordDialog(
  props: EmployeesResetPasswordDialogProps,
): React.ReactElement | null {
  const { open, onOpenChange, user } = props;
  const issueMutation = useIssueResetMutation();
  const sendEmailMutation = useSendResetEmailMutation();

  const [reveal, setReveal] = React.useState<RevealState | null>(null);
  const [emailError, setEmailError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setReveal(null);
      setEmailError(null);
    }
  }, [open]);

  const onIssue = React.useCallback(async () => {
    if (!user) return;
    try {
      const result = await issueMutation.mutateAsync({ targetUserId: user.id });
      setReveal({
        resetUrl: result.resetUrl,
        expiresAt: result.expiresAt,
        rawToken: extractRawToken(result.resetUrl),
      });
    } catch (err) {
      if (err instanceof ApiError) {
        toast(err.message, { variant: 'error' });
        return;
      }
      toast('Не удалось создать ссылку.', { variant: 'error' });
    }
  }, [issueMutation, user]);

  const onSendEmail = React.useCallback(async () => {
    if (!reveal) return;
    setEmailError(null);
    try {
      const out = await sendEmailMutation.mutateAsync({ rawToken: reveal.rawToken });
      if (out.ok) {
        toast('Email отправлен', { variant: 'success' });
        return;
      }
      if (out.error === 'smtp.unconfigured') {
        toast(
          'Сервер email не настроен. Попросите супервайзера настроить SMTP в разделе «Настройки».',
          { variant: 'warning' },
        );
        setEmailError('smtp.unconfigured');
        return;
      }
      toast(`Ошибка отправки: ${out.error ?? 'неизвестная'}`, { variant: 'error' });
      setEmailError(out.error ?? 'unknown');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Не удалось отправить email.';
      toast(msg, { variant: 'error' });
      setEmailError('network');
    }
  }, [reveal, sendEmailMutation]);

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[500px]" data-testid="employees-reset-dialog">
        {reveal === null ? (
          <>
            <DialogHeader>
              <DialogTitle data-testid="employees-reset-title">
                Сбросить пароль для {user.fullName}?
              </DialogTitle>
              <DialogDescription>
                Будет создана одноразовая ссылка для установки нового пароля. Срок действия —
                1&nbsp;час.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                data-testid="employees-reset-cancel"
              >
                Отмена
              </Button>
              <Button
                type="button"
                onClick={onIssue}
                disabled={issueMutation.isPending}
                data-testid="employees-reset-issue"
              >
                Создать ссылку
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="space-y-4" data-testid="employees-reset-reveal">
            <DialogHeader>
              <DialogTitle>Ссылка для сброса пароля</DialogTitle>
              <DialogDescription>
                Скопируйте ссылку или отправьте её на email сотрудника.
              </DialogDescription>
            </DialogHeader>
            <TokenLinkDisplay
              url={reveal.resetUrl}
              expiresAt={reveal.expiresAt}
              label="Ссылка для сброса"
              testIdPrefix="employees-reset-link"
              secondaryAction={
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  onClick={onSendEmail}
                  disabled={sendEmailMutation.isPending}
                  data-testid="employees-reset-send-email"
                  aria-label="Отправить ссылку на email"
                >
                  <Mail className="size-4" aria-hidden="true" />
                  <span>Отправить email</span>
                </Button>
              }
            />
            {emailError ? (
              <p
                role="alert"
                data-testid="employees-reset-email-error"
                className="text-xs text-destructive"
              >
                {emailError === 'smtp.unconfigured'
                  ? 'Email не отправлен — SMTP не настроен. Передайте ссылку вручную.'
                  : `Email не отправлен (${emailError}).`}
              </p>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                onClick={() => onOpenChange(false)}
                data-testid="employees-reset-close"
              >
                Закрыть
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
